import { buildInteractiveResult } from '@coveo/headless';
import classNames from 'classnames';
import { useGeolocationState } from 'contexts/GeolocationContext';
import * as customPropTypes from 'custom-prop-types';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Button } from '@/design-system/atoms/Button';
import { Loading } from '@/design-system/atoms/Loading';
import { RichTextEditor } from '@/design-system/atoms/RichTextEditor';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';
import { ProviderResultCard } from '@/design-system/molecules/ProviderResultCard';
import { ProviderScheduling } from '@/features/ProviderSearch/components/ProviderScheduling';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSearchParams } from '@/hooks/useSearchParams';
import { useTimeOfDay } from '@/hooks/useTimeOfDay';
import { arrayMatchValues } from '@/utils/arrayMatchValues';
import { getEasternDateString, getEasternDayDiff } from '@/utils/easternDate';
import { preserveHashInUrl } from '@/utils/preserveHashInUrl';

import { useProviderOutsideMatchesContext } from '../../contexts/ProviderOutsideMatchesContext';
import {
    useProviderSearchContext,
    useProviderSearchZipCityContext,
    useProviderSearchZipCodeErrorContext,
} from '../../contexts/ProviderSearchContext';
import { useProviderUIContext } from '../../contexts/ProviderUIContext';
import {
    hasVpcBannerExcludedSpecialty,
    isVpcBannerQualifyingSearch,
    isVpcQualifyingSearch,
} from '../../utils/deriveScenarioKey';
import isInMarketLocation, { isInMarketNJ } from '../../utils/isInMarketLocation';
import { ProviderResultCardWrapper } from '../ProviderResultCardWrapper';
import deriveHasVirtualCare from './deriveHasVirtualCare';
import styles from './ProviderResultsList.module.scss';
import shouldShowFewResultsMessage from './shouldShowFewResultsMessage';
import { useFacetSubscription } from './useFacetSubscription';

/**
 * Returns true when a raw Coveo result represents a Virtual Primary Care provider.
 * Handles both string and string[] shapes for raw.specialtyservices (parent A-01).
 * Never throws — a result with a missing raw object returns false.
 * Match is exact and case-sensitive against 'Virtual Care' (parent §8.4).
 *
 * Private — not exported. Not exported because it is an implementation detail of the
 * VPC-only detection algorithm; callers test the detection outcome, not the predicate.
 *
 * @param {object} result - Raw Coveo result
 * @returns {boolean}
 */
const isVpcProvider = (result) => {
    const services = result?.raw?.specialtyservices;
    if (services == null) return false;
    if (Array.isArray(services)) return services.includes('Virtual Care');
    return services === 'Virtual Care';
};

// Stable per-provider id (profile slug, permanentid fallback) so `activeProvider` survives Next Available reordering.
export const deriveStableResultId = (result) => {
    const href = result?.link?.href;
    if (typeof href === 'string' && href.length > 0) {
        const slug = href.split('/').filter(Boolean).pop();
        if (slug) {
            return slug;
        }
    }
    return result?.id;
};

const ProviderResultsList = ({
    schedulingOverrideLabels,
    cta,
    analytics,
    patientTypeId,
    newPatientAppointmentIds,
    affiliationBadgeDictionary,
}) => {
    const {
        coveo: {
            engine,
            controller: { resultList, querySummary, facet },
        },
        patientType,
        noResultsComponent,
        errorComponent,
        vpcBannerComponent,
        disableCalendar,
        disableAvailabilityMessage,
        triggerExecuteSearch,
        currentGeolocationRef,
    } = useProviderSearchContext();
    const {
        hasOutsideMatches,
        isVpcOutsideMatches,
        setIsVpcOutsideMatches,
        vpcResults,
        setVpcResults,
        setHasOutsideMatches,
    } = useProviderOutsideMatchesContext();

    const {
        notAcceptingPatientsText,
        existingPatientText,
        noOnlineSchedulingText,
        availabilityLabel,
        outsideMatchesDisabled,
        outsideMatchesBannerDescription,
        outsideMatchesResultsTitle,
        fewResultsMessage,
    } = useProviderUIContext();
    const { zipCode, city } = useProviderSearchZipCityContext();
    const zipCodeServerError = useProviderSearchZipCodeErrorContext();
    const breakpoints = useBreakpoint();
    const phase = useTimeOfDay();
    const geo = useGeolocationState();

    const [resultListState, setResultListState] = useState({});
    const [results, setResults] = useState([]);
    // True only while an append (fetchMoreResults) is in flight, distinct from a fresh-search load. AHSMKTG-4048
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [querySummaryState, setQuerySummaryState] = useState(querySummary.state);
    // Init from URL so a deep return (page=N) continues from N, not 1. AHSMKTG-3982
    const [pageNumber, setPageNumber] = useState(() => {
        const p = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
        return p > 0 ? p : 1;
    });
    const [distance, setDistance] = useState(null);
    const [allResultsAreVpc, setAllResultsAreVpc] = useState(false);
    const specialtyState = useFacetSubscription(facet.specialty);
    const specialtyServiceState = useFacetSubscription(facet.specialtyservice);
    const searchParams = useSearchParams();
    const history = useHistory();

    const bannerState = (city || currentGeolocationRef?.current?.city)?.split(',').pop()?.trim();

    // Refs keep the VPC detection callback always reading the current values without
    // re-subscribing to the Coveo result list every time these values change.
    // history is also captured via ref so the subscribe effect deps stay stable:
    // useHistory() creates a new object each render (in both production and test
    // environments), which would make history an unstable dep and cause the effect to
    // re-subscribe on every render, triggering the callback repeatedly.
    const isVpcOutsideMatchesRef = useRef(isVpcOutsideMatches);
    const historyRef = useRef(history);
    const triggerExecuteSearchRef = useRef(triggerExecuteSearch);

    useEffect(() => {
        isVpcOutsideMatchesRef.current = isVpcOutsideMatches;
    }, [isVpcOutsideMatches]);

    useEffect(() => {
        historyRef.current = history;
    }, [history]);

    useEffect(() => {
        triggerExecuteSearchRef.current = triggerExecuteSearch;
    }, [triggerExecuteSearch]);

    /**
     * Validate provider affiliations
     * @param { object } result - provider data
     * @returns {Array} the provider affiliation array
     */

    const handleAffiliationBadge = useCallback(
        (result) => {
            if (Object.keys(result).length <= 0) {
                return null;
            }

            const affiliationBadge =
                result.raw.practicegroups?.length && affiliationBadgeDictionary?.length
                    ? arrayMatchValues(
                          result.raw.practicegroups,
                          affiliationBadgeDictionary?.map((badge) => badge.value),
                      )
                    : null;

            if (affiliationBadge?.length) {
                return affiliationBadgeDictionary.filter(
                    (badge) => badge.value === affiliationBadge,
                );
            }

            return [];
        },
        [affiliationBadgeDictionary],
    );

    const buildResultsCards = useCallback(
        (s, now) => {
            const interactiveResultController = engine
                ? buildInteractiveResult(engine, {
                      options: { result: s },
                  })
                : null;
            let image;
            if (s.raw.imageurl) {
                image = {
                    defaultImgSrc: s.raw.imageurl,
                };
            }

            let location = [];
            if (s.raw.locationcitystate) {
                location = s.raw.locationcitystate;
            } else if (
                s.raw.locationcity &&
                s.raw.locationstate &&
                Array.isArray(s.raw.locationcity)
            ) {
                for (let [, city] of s.raw.locationcity.entries()) {
                    location.push(city);
                }
            }

            const affiliationBadgeLabel = handleAffiliationBadge(s);

            const link = {
                href: new URL(s.clickUri).pathname,
                target: '_self',
                csr: true,
                ariaLabel: `View profile for ${s.title}`,
            };

            const firstAvailabilityDate = s.raw.firstavailabilitydate
                ? getEasternDateString(s.raw.firstavailabilitydate)
                : null;
            const nextAvailabilityDate = s.raw.nextavailabilitydate
                ? getEasternDateString(s.raw.nextavailabilitydate)
                : null;
            let effectiveFirstAvailabilityDate = firstAvailabilityDate;

            if (s.raw.firstavailabilitydate) {
                const firstAvailabilityTimestamp = new Date(s.raw.firstavailabilitydate);
                if (
                    !isNaN(firstAvailabilityTimestamp.getTime()) &&
                    firstAvailabilityTimestamp.getTime() < now.getTime()
                ) {
                    effectiveFirstAvailabilityDate = getEasternDateString(now);
                }
            }

            const availability = {
                phoneNumber:
                    s.raw.locationphone && Array.isArray(s.raw.locationphone)
                        ? s.raw.locationphone[0]
                        : null,
                schedulingCta: {
                    href: `${new URL(s.clickUri).pathname}#scheduling`,
                    target: '_self',
                    csr: true,
                    ariaLabel: `View all times & schedule with ${s.title}`,
                },
                npi: s.raw.providernpi,
                firstAvailabilityDate,
                nextAvailabilityDate,
            };

            // Specialties to ignore
            const ignoreSpecialties = ['Primary Care'];
            // Filter out ignored specialties
            const specialties = Array.isArray(s.raw.specialties)
                ? s.raw.specialties.filter((specItem) => !ignoreSpecialties.includes(specItem))
                : [];

            // AHSMKTG-3534: Compute from firstavailabilitydate only.
            let availableSoonOnline = false;
            let availableOnline = false;

            if (effectiveFirstAvailabilityDate) {
                const diffDays = getEasternDayDiff(now, effectiveFirstAvailabilityDate);
                availableSoonOnline = diffDays >= 0 && diffDays <= 180;
                availableOnline = diffDays >= 0 && diffDays <= 545;
            }

            const hasVirtualCare = deriveHasVirtualCare(s.raw.specialtyservices);
            const locationAddressOverride = s.raw.locaddressoverridemessage;

            const item = {
                id: s.permanentid,
                image,
                acceptingNewPatients: s.raw.acceptingnewpatients === 'True' ? true : false,
                onlineBookingAvailable: s.raw.onlinebookingavailable === 'True' ? true : false,
                availableSoonOnline,
                availableOnline,
                hasVirtualCare,
                locationAddressOverride,
                title: s.title.replace(',', ', '),
                primarySpecialty: s.raw.primaryspecialty,
                specialties: specialties,
                hospitalAffiliation: s.raw.hospitalAffiliations,
                location,
                averageRating: s.raw.overallrating,
                position: {
                    lat: s.raw.latitude,
                    lng: s.raw.longitude,
                },
                affiliationBadgeLabel,
                patientType,
                link,
                availability,
                myChartUrl: s.raw.mycharturl,
                coveoAnalyticsCallbacks: interactiveResultController
                    ? {
                          cancelPendingSelect: () =>
                              interactiveResultController.cancelPendingSelect(),
                          onClick: () => interactiveResultController.select(),
                          onContextMenu: () => interactiveResultController.select(),
                          onMouseDown: () => interactiveResultController.select(),
                          onMouseUp: () => interactiveResultController.select(),
                          onTouchStart: () => interactiveResultController.beginDelayedSelect(),
                          onTouchEnd: () => interactiveResultController.cancelPendingSelect(),
                          onAnchorClick: () => interactiveResultController.select(),
                      }
                    : undefined,
            };

            return item;
        },
        [engine, handleAffiliationBadge, patientType],
    );

    useEffect(() => {
        return resultList.subscribe(() => {
            // VPC-only detection (AHSMKTG-4019).
            // Operates on raw results BEFORE card mapping — isVpcProvider reads
            // result.raw.specialtyservices which is not preserved in the mapped shape.
            //
            // Re-entrancy guard: when Scenario 3 is already active the outside-matches
            // results arrive on a second callback. Skipping detection prevents a duplicate
            // URL write or a second triggerExecuteSearch call (spec AC-11).
            const rawResults = resultList.state.results;

            // AHSMKTG-4179: only classify a result set that a search response actually
            // produced. Coveo's controller.subscribe() invokes this listener synchronously at
            // subscribe time (and again on every re-subscribe caused by a ProviderSearchContext
            // `value` rebuild), so it runs with results:[] before any response exists. Without
            // this gate, allRawResultsAreVpc was false on that pre-response state and the
            // step-3 direct-nav fallback below stripped outsideMatches from the URL, which made
            // ProviderSearchContext's mount effect re-dispatch a byte-identical probe search —
            // the duplicate Coveo request. !isLoading matters independently: while a new search
            // is in flight, state.results still holds the previous search's results, so
            // classifying then reaches a verdict on stale data.
            // This does not replace the synchronous isVpcOutsideMatchesRef assignment below —
            // that guard still covers a re-entrant notification carrying a settled result set.
            const hasSearchResponse =
                resultList.state.firstSearchExecuted && !resultList.state.isLoading;

            if (hasSearchResponse && !isVpcOutsideMatchesRef.current) {
                const urlSearchParams = new URLSearchParams(window.location.search);
                // Not Object.fromEntries: it collapses a repeated param (?specialty=A&specialty=B)
                // to the last value only, which would silently drop all but one selected facet
                // value from the direct-nav outside-matches search dispatched below. Repeats are
                // preserved as arrays, matching the shape getSearchParamsWithGeolocation builds
                // with searchParams.getAll. Single values stay scalar and are normalized by
                // triggerExecuteSearch. AHSMKTG-4179.
                const urlParams = {};
                // keys() yields a repeated key once per occurrence — dedupe so getAll runs
                // once per distinct param.
                for (const key of new Set(urlSearchParams.keys())) {
                    const allValues = urlSearchParams.getAll(key);
                    urlParams[key] = allValues.length > 1 ? allValues : allValues[0];
                }
                const isVpcQualifying = isVpcQualifyingSearch({
                    term: urlParams.term,
                    specialties: urlSearchParams.getAll('specialty'),
                });
                const isNJ = isInMarketNJ({ city: currentGeolocationRef?.current?.city });
                const allRawResultsAreVpc =
                    rawResults.length > 0 && rawResults.every((r) => isVpcProvider(r));
                // Only update allResultsAreVpc on the first-page callback (not the
                // outside-matches callback where the guard skips). This preserves the
                // true value from the initial all-VPC result set so the banner gate
                // can render in outsideResults state (AHSMKTG-4137).
                setAllResultsAreVpc(allRawResultsAreVpc);
                // isDirectNav: true when outsideMatches was already in the URL at callback
                // time (direct-navigation flow); false in the normal flow where this callback
                // fires before the URL write.
                const isDirectNav = new URLSearchParams(window.location.search).has(
                    'outsideMatches',
                );

                if (isVpcQualifying && isNJ && allRawResultsAreVpc) {
                    // Steps a and b are contractually ordered before step c (spec §3):
                    // setIsVpcOutsideMatches(true) must be visible to triggerSearch before
                    // the URL write triggers a re-entry, or triggerSearch re-runs the initial
                    // search and creates an infinite loop.
                    setVpcResults(rawResults);
                    setIsVpcOutsideMatches(true);
                    // Sync the ref immediately — React state update is async (enqueued), so
                    // the render that would update isVpcOutsideMatchesRef.current via useEffect
                    // has not occurred yet. Without this, a synchronous Coveo loading-state
                    // subscriber notification fired by triggerExecuteSearch re-enters the
                    // detection block with the ref still false and clears outsideMatches (AC-11).
                    isVpcOutsideMatchesRef.current = true;

                    if (isDirectNav) {
                        // Direct-navigation flow: triggerSearch ran the initial search without
                        // setting hasOutsideMatches. Set it explicitly and fire the
                        // outside-matches search directly — the URL already carries
                        // outsideMatches=true so no further URL write is needed.
                        setHasOutsideMatches(true);
                        triggerExecuteSearchRef.current({
                            ...urlParams,
                            distance: 'any',
                            isOutsideMatches: true,
                        });
                    } else {
                        // Normal flow: write outsideMatches to URL and let ProviderSearchContext
                        // detect it via useEffect → triggerSearch → outside-matches search.
                        // history.replace (not push) matches the zero-results trigger so the
                        // intermediate state is not added to the Back stack.
                        const url = new URL(window.location);
                        url.searchParams.set('outsideMatches', 'true');
                        historyRef.current.replace(preserveHashInUrl(url.pathname + url.search));
                    }
                } else if (isVpcQualifying && isNJ && isDirectNav && !allRawResultsAreVpc) {
                    // Direct-navigation fallback (Scenario 2): the initial search returned at
                    // least one non-VPC provider — Scenario 3 does not apply. Remove
                    // outsideMatches so triggerSearch runs a normal Scenario 2 search.
                    // The isVpcQualifying && isNJ guards prevent this branch from firing on
                    // non-VPC outside-matches callbacks (AC-15 regression guard).
                    const url = new URL(window.location);
                    url.searchParams.delete('outsideMatches');
                    historyRef.current.replace(preserveHashInUrl(url.pathname + url.search));
                }
            }

            setResultListState(resultList.state);

            const now = new Date();
            const newResults = rawResults.map((s) => buildResultsCards(s, now));

            // De-dup by stable provider identity — Coveo can return the same provider at the
            // page-1/page-2 boundary when ranking shifts between requests. AHSMKTG-4050
            const seenIds = new Set();
            const dedupedResults = newResults.filter((r) => {
                const id = deriveStableResultId(r);
                if (seenIds.has(id)) {
                    return false;
                }
                seenIds.add(id);
                return true;
            });

            // De-dup by stable provider identity — Coveo can return the same provider at the
            // page-1/page-2 boundary when ranking shifts between requests. AHSMKTG-4050
            // Removed duplicate de-duplication logic

            setResults(dedupedResults);

            if (!resultList.state.isLoading) {
                setIsLoadingMore(false);
            }
        });
    }, [
        buildResultsCards,
        currentGeolocationRef,
        resultList,
        setHasOutsideMatches,
        setIsVpcOutsideMatches,
        setVpcResults,
    ]);

    useEffect(() => {
        const unsubscribe = querySummary.subscribe(() => {
            setQuerySummaryState(querySummary.state);
        });
        return () => {
            // Unsubscribe from the query summary state
            unsubscribe();
        };
    }, [querySummary]);

    const handleLoadMoreClick = useCallback(
        (e) => {
            e.preventDefault();
            setPageNumber((prevPageNumber) => prevPageNumber + 1);

            const url = new URL(window.location);
            url.searchParams.set('page', pageNumber + 1);
            url.searchParams.delete('activeProvider');
            window.history.replaceState({}, '', url);

            setIsLoadingMore(true);
            resultList.fetchMoreResults();
        },
        [resultList, pageNumber],
    );

    /**
     * Triggers a new search with distance set to "any" after an outside matches search is returned.
     * Removes the distance parameter from the URL search params and updates the browser history,
     * then hides the outside matches display.
     */
    const triggerAllResults = useCallback(
        (e) => {
            e.preventDefault();
            const url = new URL(window.location);
            url.searchParams.delete('outsideMatches');
            url.searchParams.delete('distance');
            const newUrl = url.pathname + url.search;
            history.replace(preserveHashInUrl(newUrl));
            if (breakpoints?.large) {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            }
        },
        [history, breakpoints?.large],
    );

    const bannerPosition = useMemo(() => {
        if (phase === null) return null;
        if (phase === 'night') return 0;
        // Scenario 3: VPC providers are in vpcResults, not results — use vpcResults.length
        // for position clamping so the banner lands correctly in the VPC cards list.
        // All other cases (including Scenario 2 outsideResults) use results.length.
        const count = isVpcOutsideMatches ? vpcResults?.length ?? 0 : results.length;
        return Math.min(2, count);
    }, [phase, results.length, isVpcOutsideMatches, vpcResults?.length]);

    const state = useMemo(() => {
        const hasResultsListState = Object.keys(resultListState).length > 0;
        const firstSearchExecuted = !!resultListState?.firstSearchExecuted;
        // An append (loading-more) must not be treated as a full-page load — the results
        // already on screen stay mounted while the next page is fetched. AHSMKTG-4048 / AHSMKTG-4049
        const isFullPageLoad = resultListState.isLoading && !isLoadingMore;
        return [
            {
                state: 'results',
                valid:
                    results.length > 0 &&
                    !hasOutsideMatches &&
                    (!resultListState.isLoading || isLoadingMore),
            },
            { state: 'error', valid: resultListState.hasError || zipCodeServerError },
            {
                state: 'loading',
                valid: isFullPageLoad || !hasResultsListState || !firstSearchExecuted,
            },
            {
                state: 'noResults',
                // When Scenario 3 is active (isVpcOutsideMatches), the outside-matches search may
                // legitimately return 0 physical providers (e.g. a rural area with no non-VPC
                // providers). Suppress 'noResults' so the hybrid layout ('outsideResults') renders
                // the VPC section even when the physical-provider list is empty (spec AC-12).
                valid: hasResultsListState && results.length === 0 && !isVpcOutsideMatches,
            },
            {
                state: 'outsideResults',
                // Scenario 3 (isVpcOutsideMatches): valid even when results.length === 0 because
                // the hybrid layout renders VPC cards from vpcResults, not from results (spec AC-12).
                valid:
                    hasOutsideMatches &&
                    (results.length > 0 || isVpcOutsideMatches) &&
                    !outsideMatchesDisabled,
            },
        ].find(({ valid }) => valid)?.state;
    }, [
        resultListState,
        results.length,
        hasOutsideMatches,
        isVpcOutsideMatches,
        zipCodeServerError,
        outsideMatchesDisabled,
        isLoadingMore,
    ]);

    useEffect(() => {
        const url = new URL(window.location);

        if (!outsideMatchesDisabled) {
            if (state === 'noResults' && (zipCode || city)) {
                url.searchParams.set('outsideMatches', 'true');
                const newUrl = url.pathname + url.search;

                history.replace(preserveHashInUrl(newUrl));
                setDistance((url.searchParams.get('distance') || '').toString());
            }

            if (state === 'outsideResults' && !distance) {
                setDistance((url.searchParams.get('distance') || '').toString());
            }
        }
    }, [state, zipCode, city, history, outsideMatchesDisabled, distance]);

    if (!state) {
        return null;
    }

    const selectedSpecialties =
        specialtyState.values?.filter((v) => v.state === 'selected').map((v) => v.value) ?? [];
    const selectedSpecialtyServices =
        specialtyServiceState.values?.filter((v) => v.state === 'selected').map((v) => v.value) ??
        [];
    // VPC checkbox is applied as an AQ expression via ?vpc=true URL param (not a Coveo facet
    // selection), so facet.vpc.state.values never reaches 'selected'. Read from URL params instead.
    const isVpcChecked = searchParams.get('vpc') === 'true';

    const shouldShowVpcBanner =
        !!vpcBannerComponent &&
        bannerPosition !== null &&
        isInMarketLocation(geo) &&
        // Pediatrics suppresses the banner on every path, including outsideResults (AHSMKTG-4108).
        !hasVpcBannerExcludedSpecialty(selectedSpecialties) &&
        ((state === 'results' &&
            isVpcBannerQualifyingSearch({
                term: querySummaryState.query,
                specialties: selectedSpecialties,
                specialtyServices: selectedSpecialtyServices,
                isVpcChecked,
            })) ||
            // Scenario 3 is excluded: the banner must not appear in the in-person list.
            // It is handled separately by shouldShowVpcBannerInVpcSection below.
            (state === 'outsideResults' && allResultsAreVpc && !isVpcOutsideMatches));

    // Scenario 3: show banner in the VPC cards list only when >= 3 VPC providers were found.
    const shouldShowVpcBannerInVpcSection =
        !!vpcBannerComponent &&
        bannerPosition !== null &&
        isInMarketLocation(geo) &&
        !hasVpcBannerExcludedSpecialty(selectedSpecialties) &&
        isVpcOutsideMatches &&
        (vpcResults?.length ?? 0) >= 3;

    const baseResultsToRender = state === 'outsideResults' ? results?.slice(0, 5) : results;
    const renderedResults =
        state === 'results' || state === 'outsideResults'
            ? (baseResultsToRender ?? []).map((r) => {
                  const id = deriveStableResultId(r);
                  return (
                      <ProviderResultCardWrapper key={id} id={id}>
                          <ProviderResultCard
                              {...r}
                              newPatientAppointmentIds={newPatientAppointmentIds}
                              analytics={{
                                  ...analytics,
                                  searchTerm: querySummaryState.query,
                                  zipCode,
                              }}
                              notAcceptingPatientsText={notAcceptingPatientsText}
                              existingPatientText={existingPatientText}
                              noOnlineSchedulingText={noOnlineSchedulingText}
                              availabilityLabel={availabilityLabel}
                              disableCalendar={disableCalendar}
                              disableAvailabilityMessage={disableAvailabilityMessage}
                          />
                      </ProviderResultCardWrapper>
                  );
              })
            : [];
    if (shouldShowVpcBanner) {
        renderedResults.splice(
            bannerPosition,
            0,
            <li key="vpc-banner" className={styles['vpc-banner']}>
                {vpcBannerComponent}
            </li>,
        );
    }

    const renderedVpcCards =
        state === 'outsideResults' && isVpcOutsideMatches
            ? (vpcResults ?? []).map((rawResult) => {
                  const mappedResult = buildResultsCards(rawResult, new Date());
                  const id = deriveStableResultId(mappedResult);
                  return (
                      <ProviderResultCardWrapper key={id} id={id}>
                          <ProviderResultCard
                              {...mappedResult}
                              newPatientAppointmentIds={newPatientAppointmentIds}
                              analytics={{
                                  ...analytics,
                                  searchTerm: querySummaryState.query,
                                  zipCode,
                              }}
                              notAcceptingPatientsText={notAcceptingPatientsText}
                              existingPatientText={existingPatientText}
                              noOnlineSchedulingText={noOnlineSchedulingText}
                              availabilityLabel={availabilityLabel}
                              disableCalendar={disableCalendar}
                              disableAvailabilityMessage={disableAvailabilityMessage}
                          />
                      </ProviderResultCardWrapper>
                  );
              })
            : [];
    if (shouldShowVpcBannerInVpcSection) {
        renderedVpcCards.splice(
            bannerPosition,
            0,
            <li key="vpc-banner" className={styles['vpc-banner']}>
                {vpcBannerComponent}
            </li>,
        );
    }

    return (
        <>
            {results.length > 0 && state === 'results' && (
                <ProviderScheduling
                    id={patientTypeId}
                    analytics={analytics}
                    schedulingOverrideLabels={schedulingOverrideLabels}
                    searchValue={querySummaryState.query}
                />
            )}
            <div className={styles['provider-results-list']}>
                {state === 'outsideResults' && !isVpcOutsideMatches && (
                    <Theme
                        name="medium"
                        className={styles['provider-results-list__outside-results']}
                    >
                        <div className={styles['provider-results-list__outside-results-container']}>
                            <Title
                                tag={Title.TAG.H2}
                                variant={Title.VARIANT.H5}
                                content={`We couldn't find any providers within ${distance} miles.`}
                            />
                            {outsideMatchesBannerDescription && (
                                <RichTextEditor
                                    className={
                                        styles['provider-results-list__outside-results-text']
                                    }
                                    text={outsideMatchesBannerDescription}
                                />
                            )}
                        </div>
                    </Theme>
                )}
                {(state === 'results' || state === 'outsideResults') && (
                    <>
                        {state === 'outsideResults' && isVpcOutsideMatches && (
                            // Scenario 3 (AHSMKTG-4019): VPC section — title styled like
                            // "Matches outside your search area" (no gray banner) with the
                            // VPC cards below. role="region" goes on the <div> wrapper, not
                            // the <ul>, since region is not an allowed role on a list element.
                            <div
                                aria-live="polite"
                                role="region"
                                aria-label="Virtual care providers"
                            >
                                <Title
                                    tag={Title.TAG.H2}
                                    variant={Title.VARIANT.H4}
                                    content={`Virtual care available anywhere in ${bannerState}`}
                                    className={
                                        styles['provider-results-list__outside-results-title']
                                    }
                                />
                                {renderedVpcCards.length > 0 && (
                                    <ul className={styles['provider-results-list__wrapper']}>
                                        {renderedVpcCards}
                                    </ul>
                                )}
                            </div>
                        )}

                        {state === 'outsideResults' &&
                            outsideMatchesResultsTitle &&
                            results.length > 0 && (
                                <Title
                                    tag={Title.TAG.H2}
                                    variant={Title.VARIANT.H4}
                                    content={
                                        isVpcOutsideMatches
                                            ? 'Matches for in-person care outside of your search area'
                                            : outsideMatchesResultsTitle
                                    }
                                    className={
                                        styles['provider-results-list__outside-results-title']
                                    }
                                />
                            )}

                        <ul className={styles['provider-results-list__wrapper']}>
                            {renderedResults}
                        </ul>
                        {((resultListState.moreResultsAvailable && state === 'results') ||
                            (state === 'outsideResults' && results.length > 5)) && (
                            <div
                                className={styles['provider-results-list__load-more']}
                                aria-busy={isLoadingMore || undefined}
                            >
                                <Button
                                    label={cta?.label ? cta.label : 'More Results'}
                                    ariaLabel={cta?.ariaLabel ? cta.ariaLabel : 'Load More Results'}
                                    buttonStyle={Button.STYLE.SECONDARY}
                                    onClick={
                                        state === 'outsideResults'
                                            ? triggerAllResults
                                            : handleLoadMoreClick
                                    }
                                    disabled={resultListState.isLoading}
                                    analytics={{
                                        ...analytics,
                                        eventName: 'search',
                                        componentName: 'fad_results_list',
                                        searchTerm: querySummaryState.query,
                                    }}
                                />
                                <span
                                    role="status"
                                    aria-hidden={!isLoadingMore}
                                    className={classNames(
                                        styles['provider-results-list__load-more-indicator'],
                                        !isLoadingMore &&
                                            styles[
                                                'provider-results-list__load-more-indicator--hidden'
                                            ],
                                    )}
                                >
                                    <Loading description="Loading more results..." />
                                </span>
                            </div>
                        )}
                    </>
                )}

                {state === 'error' && <>{errorComponent}</>}

                {state === 'loading' && (
                    <div className={styles['provider-results-list__overlay']}>
                        <Loading />
                    </div>
                )}

                {state === 'noResults' && state !== 'outsideResults' && <>{noResultsComponent}</>}

                {shouldShowFewResultsMessage({
                    state,
                    resultsCount: results.length,
                    fewResultsMessage,
                }) && (
                    <p className={styles['provider-results-list__few-results-message']}>
                        {fewResultsMessage}
                    </p>
                )}
            </div>
        </>
    );
};

ProviderResultsList.propTypes = {
    /**
     * Call to action for loading more results
     */
    cta: PropTypes.shape(Button.propTypes),
    analytics: customPropTypes.analyticsPropType,
    patientTypeId: PropTypes.string,
    schedulingOverrideLabels: PropTypes.shape({
        eyebrow: PropTypes.string,
        description: PropTypes.string,
        newPatientLabel: PropTypes.string,
        existingPatientLabel: PropTypes.string,
        dropdownLabel: PropTypes.string,
    }),
    affiliationBadgeDictionary: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            value: PropTypes.string,
        }),
    ),
};

export default ProviderResultsList;
